import { ObjectUpdatedType } from '@/enums/ObjectUpdatedType.enum';
import { AppConstants } from '@/helpers/app.constants';
import { PropertyHelper } from '@/helpers/property.helper';
import { Utils } from '@/helpers/utils';
import { AlertService } from '@/services/alert.service';
import { AppConfigService } from '@/services/app-config.service';
import { formatDate, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  Document,
  DocumentSummary,
  PropertiesService,
  PropertyWork,
  RealProperty,
  VaultService
} from '@eemo/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { InputDecimalValueDirective } from '../../extensions/inputDecimalValueDirective';
import { InputNullValueDirective } from '../../extensions/inputNullValueDirective';
import { InputTrimDirective } from '../../extensions/inputTrimDirective';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';
import { DocAddWidgetComponent } from '../doc-add-widget/doc-add-widget.component';

export class PropertyWorkDialogData {
  prop: RealProperty;
  work?: PropertyWork;
  edition = false;
}

@Component({
  selector: 'app-property-work-dialog',
  templateUrl: './property-work-dialog.component.html',
  styleUrls: ['./property-work-dialog.component.scss'],
  standalone: true,
  imports: [
    ModalBaseComponent,
    NgTemplateOutlet,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    InputTrimDirective,
    NgIf,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    InputNullValueDirective,
    InputDecimalValueDirective,
    DocAddWidgetComponent,
    TranslateModule
  ]
})
export class PropertyWorkDialogComponent implements OnInit, OnDestroy {
  isBrowserSupportsDateInput = Utils.isBrowserSupportsDateInput;

  subs: Subscription[] = [];

  workForm: UntypedFormGroup;
  dialogTitle: string;
  formSubmitting = false;

  documents: Document[] = [];
  newDocuments: DocumentSummary[] = [];
  // array used to simulate a classic for loop (for(int i=0;i<5;i++)
  newDocumentsCount = [1];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PropertyWorkDialogData,
    private propertyService: PropertiesService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    public propertyHelper: PropertyHelper,
    public dialogRef: MatDialogRef<PropertyWorkDialogComponent>,
    private appConfigService: AppConfigService,
    private vaultService: VaultService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.dialogTitle = this.data.edition ? 'Modifier les travaux' : 'Ajouter des travaux';
  }

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.workForm.get(field);
  }

  createDateNotFutureValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }
      if (new Date(value) > new Date()) return { dateFuture: true };
      return null;
    };
  }

  ngOnInit() {
    this.workForm = this.fb.group({
      summary: [null, [Validators.required, Validators.maxLength(50)]],
      type: [null, Validators.required],
      dateStart: [null, [Validators.required, this.createDateNotFutureValidator()]],
      dateEnd: [null, Validators.required],
      description: [null, Validators.maxLength(160)],
      energyImpact: [false],
      cost: [null, [Validators.pattern(AppConstants.FORM_PATTERN_POSITIVE_DECIMAL), Validators.min(0)]],
      company: [null, Validators.maxLength(50)]
    });

    if (this.data.work) {
      this.workForm.patchValue(this.data.work);
      this.data.work.documents.forEach((d) => {
        this.subs.push(this.vaultService.getDocument(d.uid).subscribe((doc) => this.documents.push(doc)));
      });
    }
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }

  onNewDocSelected(doc: DocumentSummary) {
    if (doc) {
      this.newDocuments.push(doc);
      this.newDocumentsCount.push(1);
    }
  }

  onExistingDocDeleted(doc: DocumentSummary) {
    if (doc) {
      // doc deleted
      this.documents = this.documents.filter((d) => d.uid != doc.uid);
    }
  }

  onNewDocDeleted(doc: DocumentSummary) {
    if (doc) {
      // new doc deleted
      this.newDocuments = this.newDocuments.filter((d) => d.uid != doc.uid);
      this.newDocumentsCount.pop();
    }
  }

  onSubmit() {
    // parse the date to format date send by mat datepicker (browser with support of input type="date" doesn't need it)
    const dateStart = new Date(this.f('dateStart').value);
    const dateEnd = new Date(this.f('dateEnd').value);

    if (dateStart > dateEnd) {
      this.f('dateEnd').setErrors({ beforeDateStart: true });
    }

    if (this.workForm.valid) {
      this.formSubmitting = true;
      this.f('dateEnd').setValue(formatDate(dateEnd, 'yyyy-MM-dd', this.locale));
      this.f('dateStart').setValue(formatDate(dateStart, 'yyyy-MM-dd', this.locale));

      const work: PropertyWork = this.workForm.value;

      work.documents = [];
      this.documents.forEach((d) => work.documents.push(this.propertyHelper.mapToDocumentSummary(d)));
      this.newDocuments.forEach((d) => work.documents.push(d));

      if (this.data.edition) {
        this.subs.push(
          this.propertyService.updateRealPropertyWork(this.data.prop.uid, this.data.work.uid, work).subscribe({
            next: () => {
              this.dialogRef.close(true);
              this.alertService.success('Mise à jour effectuée');
              this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.WORK);
            },
            error: () => {
              this.dialogRef.close();
            }
          })
        );
      } else {
        this.subs.push(
          this.propertyService.createRealPropertyWork(this.data.prop.uid, work).subscribe({
            next: (createdId) => {
              this.dialogRef.close(createdId);
              this.alertService.success('Création effectuée');
              this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.WORK);
            },
            error: () => {
              this.dialogRef.close();
            }
          })
        );
      }
    }
  }
  onNoClick(): void {
    this.dialogRef.close(false);
  }
}
